import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useGitHub, useGitHubOrgs, FEATURED_REPOS, getUniqueLanguages, formatLastUpdated } from '../../hooks/useGitHub';

const INITIAL_DISPLAY_COUNT = 10;

const Projects = () => {
  const { repos, loading, error } = useGitHub('danielostrow');
  const { orgs, loading: orgsLoading } = useGitHubOrgs('danielostrow');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');
  const [showAllProjects, setShowAllProjects] = useState(false);

  const languages = useMemo(() => getUniqueLanguages(repos), [repos]);

  const filteredRepos = useMemo(() => {
    let filtered = repos;

    if (selectedLanguage !== 'all') {
      filtered = repos.filter(
        (repo) => repo.language?.toLowerCase() === selectedLanguage.toLowerCase()
      );
    }

    // Sort: featured first, then by stars, then by update date
    return filtered.sort((a, b) => {
      const aFeatured = FEATURED_REPOS.includes(a.name);
      const bFeatured = FEATURED_REPOS.includes(b.name);

      if (aFeatured && !bFeatured) return -1;
      if (!aFeatured && bFeatured) return 1;

      if (b.stargazers_count !== a.stargazers_count) {
        return b.stargazers_count - a.stargazers_count;
      }

      return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    });
  }, [repos, selectedLanguage]);

  const displayedRepos = showAllProjects ? filteredRepos : filteredRepos.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMoreProjects = filteredRepos.length > INITIAL_DISPLAY_COUNT;

  const getLanguageColor = (language: string | null) => {
    const colors: { [key: string]: string } = {
      Python: 'text-quantum-blue',
      JavaScript: 'text-neural-orange',
      TypeScript: 'text-quantum-blue',
      Go: 'text-quantum-green',
      'C++': 'text-quantum-purple',
      Shell: 'text-text-secondary',
      Dockerfile: 'text-quantum-blue',
    };
    return colors[language || ''] || 'text-text-secondary';
  };

  if (loading) {
    return (
      <section id="projects" className="section-padding bg-bg-dark">
        <div className="container-custom text-center">
          <div className="animate-pulse">
            <div className="h-12 bg-glass-bg rounded w-64 mx-auto mb-8"></div>
            <div className="h-6 bg-glass-bg rounded w-96 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="projects" className="section-padding bg-bg-dark">
        <div className="container-custom text-center">
          <p className="text-red-500">Failed to load projects. Please try again later.</p>
        </div>
      </section>
    );
  }

  return (
    <section id="projects" className="section-padding bg-bg-dark">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-6 md:mb-12">
          <h2 className="text-3xl md:text-5xl font-heading font-bold mb-2 md:mb-4">
            Featured <span className="gradient-text">Projects</span>
          </h2>
          <p className="text-sm md:text-lg text-text-secondary max-w-2xl mx-auto">
            Each project represents a unique technical challenge and learning opportunity.
          </p>
        </div>

        {/* Patents Section */}
        <div className="mb-6 md:mb-12">
          <div className="glass p-4 md:p-8 rounded-xl">
            <h3 className="text-lg md:text-2xl font-heading font-semibold mb-3 md:mb-6 text-center">
              <span className="gradient-text">Patents</span>
            </h3>
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-glass-bg p-4 md:p-6 rounded-xl border border-glass-border hover:border-quantum-purple transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="text-base md:text-lg font-heading font-semibold text-quantum-purple">
                    System and Method for Distributed Data Governance with Intelligent Contract Enforcement and Real-Time ETL Pipeline Monitoring
                  </h4>
                  <span className="px-2 py-1 bg-neural-orange/20 text-neural-orange text-[10px] md:text-xs rounded shrink-0 ml-2">
                    Pending
                  </span>
                </div>
                <p className="text-xs md:text-sm text-text-secondary mb-3">
                  63/988,473 · Filed Feb 23, 2026
                </p>
                <p className="text-text-secondary text-xs md:text-sm">
                  A distributed data governance system using a hybrid architecture of lightweight client agents and a centralized control plane. Agents collect metadata and compute fingerprints from ETL pipelines while a custom reasoning engine built on Mangle performs temporal inference over data contracts, schema drift detection, PII classification, and quality rule enforcement with cryptographic audit trails via Merkle logs.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="bg-glass-bg p-4 md:p-6 rounded-xl border border-glass-border hover:border-quantum-purple transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="text-base md:text-lg font-heading font-semibold text-quantum-purple">
                    Bio-Electronic Computing Device and Method of Manufacture Using Mycelium-Infused Additive Manufacturing
                  </h4>
                  <span className="px-2 py-1 bg-neural-orange/20 text-neural-orange text-[10px] md:text-xs rounded shrink-0 ml-2">
                    Pending
                  </span>
                </div>
                <p className="text-xs md:text-sm text-text-secondary mb-3">
                  63/940,146 · Filed Dec 13, 2025
                </p>
                <p className="text-text-secondary text-xs md:text-sm">
                  A bio-electronic computing device manufactured through a dual-filament 3D printing process that integrates living fungal networks with light-activated switching elements. The system uses biological growth to complete electronic circuits, creating devices capable of neuromorphic computation, environmental sensing, and decentralized network communication.
                </p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* GitHub Contribution Chart & Organizations */}
        <div className="mb-6 md:mb-12 space-y-4 md:space-y-8">
          {/* Contribution Chart */}
          <div className="glass p-4 md:p-8 rounded-xl">
            <h3 className="text-lg md:text-2xl font-heading font-semibold mb-3 md:mb-6 text-center">
              GitHub <span className="gradient-text">Activity</span>
            </h3>
            <div className="flex justify-center overflow-x-auto">
              <img
                src="https://ghchart.rshah.org/4f46e5/danielostrow"
                alt="Daniel Ostrow's GitHub Contribution Chart"
                width="896"
                height="112"
                className="rounded-lg max-w-full"
                style={{ imageRendering: 'crisp-edges' }}
                loading="lazy"
              />
            </div>
          </div>

          {/* Organizations - Hidden on mobile */}
          {!orgsLoading && orgs.length > 0 && (
            <div className="hidden md:block glass p-8 rounded-xl">
              <h3 className="text-2xl font-heading font-semibold mb-6 text-center">
                Contributing to <span className="gradient-text">Organizations</span>
              </h3>
              <div className="flex flex-wrap justify-center gap-6">
                {orgs.map((org) => (
                  <a
                    key={org.id}
                    href={`https://github.com/${org.login}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col items-center gap-3 p-4 bg-glass-bg rounded-xl hover:bg-quantum-blue/10 transition-all duration-300 hover:scale-105"
                  >
                    <img
                      src={org.avatar_url}
                      alt={org.login}
                      className="w-16 h-16 rounded-full border-2 border-glass-border group-hover:border-quantum-blue transition-colors"
                    />
                    <div className="text-center">
                      <p className="font-semibold text-quantum-blue group-hover:text-quantum-green transition-colors">
                        {org.login}
                      </p>
                      {org.description && (
                        <p className="text-xs text-text-secondary mt-1 max-w-[200px] line-clamp-2">
                          {org.description}
                        </p>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Language Filter */}
        <div className="flex flex-wrap gap-2 justify-center mb-6 md:mb-12">
          <button
            onClick={() => setSelectedLanguage('all')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ${
              selectedLanguage === 'all'
                ? 'bg-quantum-blue text-bg-dark'
                : 'bg-glass-bg text-text-secondary hover:text-quantum-blue'
            }`}
          >
            All
          </button>
          {languages.map((lang) => (
            <button
              key={lang}
              onClick={() => setSelectedLanguage(lang)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-all duration-200 ${
                selectedLanguage === lang
                  ? 'bg-quantum-blue text-bg-dark'
                  : 'bg-glass-bg text-text-secondary hover:text-quantum-blue'
              }`}
            >
              {lang}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
          {displayedRepos.map((repo, index) => (
            <motion.div
              key={repo.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.3) }}
              viewport={{ once: true }}
              className="group"
            >
              <a
                href={repo.html_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block h-full"
              >
                <div className="glass p-4 md:p-6 rounded-xl h-full flex flex-col hover:border-quantum-blue transition-all duration-300 md:hover:scale-105 hover:shadow-lg hover:shadow-quantum-blue/20">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-base md:text-xl font-heading font-semibold text-quantum-blue group-hover:text-quantum-green transition-colors truncate pr-2">
                      {repo.name}
                    </h3>
                    {FEATURED_REPOS.includes(repo.name) && (
                      <span className="px-1.5 py-0.5 bg-neural-orange/20 text-neural-orange text-[10px] md:text-xs rounded shrink-0">
                        Featured
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-text-secondary text-xs md:text-sm mb-3 flex-grow line-clamp-2">
                    {repo.description || 'No description available'}
                  </p>

                  {/* Topics - Hidden on mobile */}
                  {repo.topics.length > 0 && (
                    <div className="hidden md:flex flex-wrap gap-2 mb-3">
                      {repo.topics.slice(0, 3).map((topic) => (
                        <span
                          key={topic}
                          className="px-2 py-1 bg-quantum-purple/10 text-quantum-purple text-xs rounded"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Stats */}
                  <div className="flex items-center gap-3 text-xs md:text-sm text-text-secondary">
                    {repo.language && (
                      <span className="flex items-center gap-1">
                        <span className={`w-2 h-2 md:w-3 md:h-3 rounded-full ${getLanguageColor(repo.language)}`} style={{ backgroundColor: 'currentColor', opacity: 0.6 }}></span>
                        {repo.language}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3 md:w-4 md:h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      {repo.stargazers_count}
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3 md:w-4 md:h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.707 3.293a1 1 0 010 1.414L5.414 7H11a7 7 0 017 7v2a1 1 0 11-2 0v-2a5 5 0 00-5-5H5.414l2.293 2.293a1 1 0 11-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {repo.forks_count}
                    </span>
                    {/* Last Updated - inline on mobile */}
                    <span className="ml-auto text-[10px] md:hidden">
                      {formatLastUpdated(repo.updated_at)}
                    </span>
                  </div>

                  {/* Last Updated - Desktop only */}
                  <div className="hidden md:block mt-3 pt-3 border-t border-glass-border">
                    <span className="text-xs text-text-secondary">
                      {formatLastUpdated(repo.updated_at)}
                    </span>
                  </div>
                </div>
              </a>
            </motion.div>
          ))}
        </div>

        {/* Show More/Less Toggle */}
        {hasMoreProjects && (
          <div className="text-center mt-6 md:mt-8">
            <button
              onClick={() => setShowAllProjects(!showAllProjects)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm text-quantum-blue hover:text-quantum-green transition-colors duration-200"
            >
              <span>{showAllProjects ? 'Show Less' : `Show More (${filteredRepos.length - INITIAL_DISPLAY_COUNT} more)`}</span>
              <svg
                className={`w-4 h-4 transition-transform duration-300 ${showAllProjects ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        )}

        {/* View All on GitHub */}
        <div className="text-center mt-8 md:mt-12">
          <a
            href="https://github.com/danielostrow"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary inline-block text-sm md:text-base"
          >
            View All on GitHub →
          </a>
        </div>
      </div>
    </section>
  );
};

export default Projects;
